import * as AlertDialog from '@radix-ui/react-alert-dialog';
import {
  type PolicyDocument,
  PolicyDocumentList as PolicyDocumentListSchema,
} from '@uit/shared/schemas/policy-document';
import { useRef, useState } from 'react';
import { useApi } from '../../lib/api.js';
import s from './PolicyDocumentsSection.module.scss';

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function PolicyDocumentsSection() {
  const { data, error, loading, refetch } = useApi<PolicyDocument[]>(
    '/api/policy-documents',
    PolicyDocumentListSchema,
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PolicyDocument | null>(null);

  async function upload(file: File): Promise<void> {
    setBusy(true);
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/policy-documents', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        setMsg(
          res.status === 415
            ? 'Only PDF files are allowed.'
            : res.status === 413
              ? 'File is too large (max 10 MB).'
              : `Upload failed (${res.status}).`,
        );
        return;
      }
      void refetch();
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function toggleActive(doc: PolicyDocument): Promise<void> {
    await fetch(`/api/policy-documents/${doc.id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ active: !doc.active }),
    });
    void refetch();
  }

  async function reorder(index: number, dir: -1 | 1): Promise<void> {
    if (!data) return;
    const target = index + dir;
    if (target < 0 || target >= data.length) return;
    const ids = data.map((d) => d.id);
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    await fetch('/api/policy-documents/reorder', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    void refetch();
  }

  async function confirmDelete(): Promise<void> {
    if (!deleteTarget) return;
    await fetch(`/api/policy-documents/${deleteTarget.id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    setDeleteTarget(null);
    void refetch();
  }

  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>Policy documents</h2>
      <p className={s.sectionSubtitle}>
        PDFs attached to the post-signature “Security Policies &amp; Engagement Guidelines” email.
        Only the active set is sent, in the order shown.
      </p>

      <div className={s.uploadRow}>
        <input
          ref={inputRef}
          id="policy-upload"
          type="file"
          accept="application/pdf"
          className={s.hiddenInput}
          disabled={busy}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void upload(f);
          }}
        />
        <label htmlFor="policy-upload" className={s.uploadBtn} aria-disabled={busy}>
          {busy ? 'Uploading…' : '+ Upload PDF'}
        </label>
        {msg && (
          <span className={s.msg} role="alert">
            {msg}
          </span>
        )}
      </div>

      {error && <div className={s.error}>Failed to load policy documents: {error.message}</div>}
      {!error && loading && !data && <div className={s.loading}>Loading…</div>}
      {data && data.length === 0 && <div className={s.empty}>No policy documents yet.</div>}

      {data && data.length > 0 && (
        <ul className={s.list}>
          {data.map((doc, i) => (
            <li key={doc.id} className={doc.active ? s.row : `${s.row} ${s.rowInactive}`}>
              <div className={s.reorder}>
                <button
                  type="button"
                  className={s.iconBtn}
                  aria-label={`Move ${doc.original_filename} up`}
                  disabled={i === 0}
                  onClick={() => void reorder(i, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className={s.iconBtn}
                  aria-label={`Move ${doc.original_filename} down`}
                  disabled={i === data.length - 1}
                  onClick={() => void reorder(i, 1)}
                >
                  ↓
                </button>
              </div>
              <div className={s.meta}>
                <a
                  className={s.fileName}
                  href={`/api/policy-documents/${doc.id}/download`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {doc.original_filename}
                </a>
                <span className={s.fileSize}>{humanSize(doc.size_bytes)}</span>
              </div>
              <label className={s.toggle}>
                <input
                  type="checkbox"
                  checked={doc.active}
                  onChange={() => void toggleActive(doc)}
                  aria-label={`${doc.original_filename} active`}
                />
                Active
              </label>
              <button
                type="button"
                className={s.deleteBtn}
                onClick={() => setDeleteTarget(doc)}
                aria-label={`Delete ${doc.original_filename}`}
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}

      <AlertDialog.Root
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialog.Portal>
          <AlertDialog.Overlay className={s.overlay} />
          <AlertDialog.Content className={s.dialog}>
            <AlertDialog.Title className={s.dialogTitle}>
              Delete this policy document?
            </AlertDialog.Title>
            <AlertDialog.Description className={s.dialogBody}>
              <strong>{deleteTarget?.original_filename}</strong> will be removed and will no longer
              attach to the post-signature email.
            </AlertDialog.Description>
            <div className={s.dialogActions}>
              <AlertDialog.Cancel asChild>
                <button type="button" className={s.cancel}>
                  Keep
                </button>
              </AlertDialog.Cancel>
              <button type="button" className={s.confirm} onClick={() => void confirmDelete()}>
                Delete
              </button>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </section>
  );
}
